import React, { useState, useEffect } from 'react';
import { CheckCircle, XCircle, Loader } from 'lucide-react';
import { useLanguage } from './LanguageContext';
import { API_URL } from '../utils/api';

const VerifyEmail = ({ onNavigate }) => {
  const { print_lang } = useLanguage();
  const [status, setStatus] = useState('loading'); // loading, success, error
  const [message, setMessage] = useState('');

  useEffect(() => {
    const verify = async () => {
      // Get token from URL
      const params = new URLSearchParams(window.location.search);
      const token = params.get('token');

      if (!token) {
        setStatus('error');
        setMessage(print_lang('invalid_token', 'Invalid verification link'));
        return;
      }

      try {
        const response = await fetch(`${API_URL}/api/dentists/verify-email`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token })
        });

        const data = await response.json();

        if (data.success) {
          setStatus('success');
          setMessage(print_lang('email_verified', 'Email verified successfully! You can now log in.'));
        } else {
          setStatus('error');
          setMessage(data.message || print_lang('email_verification_failed', 'Email verification failed'));
        }
      } catch (err) {
        setStatus('error');
        setMessage(print_lang('connection_failed', 'Connection failed. Please try again.'));
      }
    };
    verify();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-8 text-center animate-slideUp">
        {status === 'loading' && (
          <>
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Loader className="w-8 h-8 text-blue-600 animate-spin" />
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">
              {print_lang('verifying_email', 'Verifying Email...')}
            </h2>
            <p className="text-gray-600">
              {print_lang('please_wait', 'Please wait')}
            </p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">
              {print_lang('verification_success', 'Verification Successful!')}
            </h2>
            <p className="text-gray-600 mb-6">{message}</p>
            <button
              onClick={() => onNavigate('login')}
              className="w-full bg-blue-600 text-white py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors"
            >
              {print_lang('go_to_login', 'Go to Login')}
            </button>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="w-16 h-16 bg-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <XCircle className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">
              {print_lang('verification_failed', 'Verification Failed')}
            </h2>
            <p className="text-gray-600 mb-6">{message}</p>
            <button
              onClick={() => onNavigate('login')}
              className="w-full bg-blue-600 text-white py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors"
            >
              {print_lang('back_to_login', 'Back to Login')}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default VerifyEmail;
